import * as React from 'react'
import { ChevronRight as CaretRightIcon, Search as MagnifyingGlassIcon } from 'lucide-react'
import {
  stringifyTagValue,
  useGetPlantLiveTagValuesByIdQuery,
  type PlantEquipmentProperty,
  type PlantTag,
  type PlantTagIdValue,
  type PlantTagValue
} from '@axilon-apps/domains-ontology-ui-state'
import { Badge } from '../../components/ui/badge'
import { cn } from '../../lib/utils'
import PlantTagHealth from './PlantTagHealth'
import { SessionEntityMark, SessionGhostRow, useVisibleGhosts } from './session-mark-ui'

/**
 * Sticky right-rail live-tag panel used on `LineDetailPage` and
 * `EquipmentDetailPage`. Reads registered tags by graph node ID. Unregistered
 * graph tags remain visible but never claim a path-matched registry row. The
 * selected tag's detail card shows the latest numeric value, unit, quality,
 * and associated range metadata.
 *
 * The rail collapses to a compact pill pinned to the host's top-right
 * corner. The panel stays mounted through the collapse so its width and
 * opacity animate smoothly. Expand/collapse is persisted in localStorage
 * so navigating between plant detail pages keeps the user's preference.
 */
export interface LiveTagPanelProps {
  title: string
  tags: PlantTag[]
  /**
   * Optional property metadata for equipment scopes — lets the panel
   * label tags as "Setpoint" / "Observable" and pull range bounds for
   * the range bar without a second query.
   */
  properties?: PlantEquipmentProperty[]
  className?: string
  /**
   * Tag id to select on first render. Defaults to the first tag.
   */
  initialSelectedTagId?: string
  /** Container entity for session-deleted tag ghosts (line or equipment). */
  scopeEntityId?: string
}

const POLL_INTERVAL_MS = 5_000

/** Shared plant preference — one key across line/equipment detail pages. */
const LIVE_TAG_PANEL_OPEN_KEY = 'ontology.plant.liveTagPanel.open'

function loadLiveTagPanelOpen(): boolean {
  try {
    const stored = window.localStorage.getItem(LIVE_TAG_PANEL_OPEN_KEY)
    if (stored === 'false') return false
    if (stored === 'true') return true
  } catch {
    // localStorage unavailable — fall through to default
  }
  return true
}

function persistLiveTagPanelOpen(open: boolean): void {
  try {
    window.localStorage.setItem(LIVE_TAG_PANEL_OPEN_KEY, open ? 'true' : 'false')
  } catch {
    // best-effort; persistence is a convenience, not a correctness requirement
  }
}

export function LiveTagPanel({
  title,
  tags,
  properties,
  className,
  initialSelectedTagId,
  scopeEntityId
}: LiveTagPanelProps): React.ReactElement {
  const [open, setOpenState] = React.useState(loadLiveTagPanelOpen)
  const setOpen = React.useCallback((next: boolean) => {
    setOpenState(next)
    persistLiveTagPanelOpen(next)
  }, [])
  const [query, setQuery] = React.useState('')
  const [selectedId, setSelectedId] = React.useState<string | undefined>(
    initialSelectedTagId ?? tags[0]?.id
  )
  const scopeGhosts = useVisibleGhosts(scopeEntityId ?? '')
  const tagGhosts = React.useMemo(() => {
    if (!scopeEntityId) return []
    const q = query.trim().toLowerCase()
    return scopeGhosts.filter((ghost) => {
      if (!ghost.labels.includes('Tag') && !ghost.labels.includes('UDTInstance')) return false
      if (!q) return true
      return (ghost.name ?? ghost.id).toLowerCase().includes(q)
    })
  }, [query, scopeEntityId, scopeGhosts])

  React.useEffect(() => {
    if (selectedId && tags.some((t) => t.id === selectedId)) return
    setSelectedId(tags[0]?.id)
  }, [tags, selectedId])

  const propertiesByTagId = React.useMemo(() => {
    const map = new Map<string, PlantEquipmentProperty>()
    for (const property of properties ?? []) {
      if (property.tag) map.set(property.tag.id, property)
    }
    return map
  }, [properties])

  const nodeIds = React.useMemo(
    () => tags.filter((tag) => tag.label !== 'UDTInstance' && tag.id).map((tag) => tag.id),
    [tags]
  )
  const idQuery = useGetPlantLiveTagValuesByIdQuery(
    { node_ids: nodeIds },
    {
      pollingInterval: nodeIds.length > 0 ? POLL_INTERVAL_MS : 0,
      skip: nodeIds.length === 0
    }
  )
  const valuesByNodeId = React.useMemo(() => {
    const map = new Map<string, PlantTagIdValue>()
    for (const value of idQuery.data?.tags ?? []) map.set(value.nodeId, value)
    return map
  }, [idQuery.data])

  const valueForTag = React.useCallback(
    (tag: PlantTag): PlantTagValue | undefined => {
      const idValue = valuesByNodeId.get(tag.id)
      if (!idValue) return undefined
      return {
        path: idValue.path || tagPathFor(tag) || tag.id,
        value: idValue.value,
        quality: idValue.quality,
        timestamp: idValue.timestamp,
        error:
          idValue.error ?? (idValue.unregistered ? 'Tag is not registered for reads' : undefined)
      }
    },
    [valuesByNodeId]
  )

  const isUnregisteredTag = React.useCallback(
    (tag: PlantTag): boolean => valuesByNodeId.get(tag.id)?.unregistered === true,
    [valuesByNodeId]
  )

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return tags
    return tags.filter((t) => {
      return (
        t.name.toLowerCase().includes(q) ||
        (t.tag_name ?? '').toLowerCase().includes(q) ||
        (t.folder_name ?? '').toLowerCase().includes(q) ||
        (t.provider_name ?? '').toLowerCase().includes(q) ||
        (t.unit ?? '').toLowerCase().includes(q)
      )
    })
  }, [tags, query])

  const selected = React.useMemo(
    () => tags.find((t) => t.id === selectedId) ?? tags[0],
    [tags, selectedId]
  )
  const selectedValue = selected ? valueForTag(selected) : undefined
  const selectedIsUnregistered = selected ? isUnregisteredTag(selected) : false
  const selectedProperty = selected ? propertiesByTagId.get(selected.id) : undefined

  return (
    <>
      {!open && (
        <div className="absolute right-0 top-0 z-10 animate-in fade-in slide-in-from-right-2 duration-300 motion-reduce:animate-none">
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="group inline-flex h-8 cursor-pointer items-center gap-2 rounded-full border bg-card pl-3 pr-2 text-card-foreground shadow-sm transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/50"
            aria-label={`Show live tag panel (${tags.length} ${tags.length === 1 ? 'tag' : 'tags'})`}
            title={`Show live tags (${tags.length})`}
          >
            <span className="relative flex size-2 shrink-0" aria-hidden>
              <span className="absolute inline-flex size-full animate-ping rounded-full bg-emerald-500 opacity-50 motion-reduce:hidden" />
              <span className="relative inline-flex size-2 rounded-full bg-emerald-500" />
            </span>
            <span className="whitespace-nowrap text-12 font-semibold text-ink-2">Live tags</span>
            <span className="rounded-full bg-bg-elev px-1.5 font-mono text-11 leading-4 text-ink-3">
              {tags.length}
            </span>
            <CaretRightIcon
              size={14}
              className="shrink-0 text-ink-3 transition-transform duration-200 group-hover:translate-x-0.5 motion-reduce:transition-none"
            />
          </button>
        </div>
      )}
      <aside
        className={cn(
          'sticky top-4 self-start overflow-hidden transition-[width,max-height,opacity,margin,visibility] duration-300 ease-out motion-reduce:transition-none',
          open
            ? 'w-full max-h-[calc(100vh-8.75rem)] opacity-100 lg:w-90'
            : 'pointer-events-none invisible w-full max-h-0 opacity-0 lg:-ml-4 lg:w-0',
          className
        )}
        data-state={open ? 'open' : 'collapsed'}
        aria-hidden={!open}
      >
        <div className="flex max-h-[calc(100vh-8.75rem)] min-h-0 w-full flex-col overflow-hidden rounded-xl border bg-card text-card-foreground shadow-sm lg:w-90">
          <div className="flex min-w-0 shrink-0 items-center gap-2 border-b px-3.5 py-3">
            <span className="size-2 shrink-0 rounded-full bg-emerald-500" aria-hidden />
            <span className="min-w-0 flex-1 truncate text-13 font-semibold text-ink">{title}</span>
            <span className="shrink-0 font-mono text-11 text-ink-3">
              {tags.length} {tags.length === 1 ? 'tag' : 'tags'}
            </span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Collapse live tag panel"
              className="grid size-6 shrink-0 place-items-center rounded-md hover:bg-accent"
            >
              <CaretRightIcon size={12} className="text-ink-3" />
            </button>
          </div>

          {selected && (
            <SelectedTagDetail
              tag={selected}
              value={selectedValue}
              property={selectedProperty}
              unregistered={selectedIsUnregistered}
            />
          )}

          <div className="shrink-0 border-b px-2.5 py-2">
            <div className="flex items-center gap-1.5 rounded-md border bg-bg-elev px-2 py-1.5">
              <MagnifyingGlassIcon size={12} className="text-ink-3" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search tags…"
                className="w-full border-0 bg-transparent text-13 text-ink outline-none placeholder:text-ink-3"
              />
            </div>
          </div>

          <div className="flex min-h-0 flex-1 flex-col gap-1.5 overflow-y-auto p-2">
            {filtered.length === 0 && tagGhosts.length === 0 ? (
              <div className="px-2 py-3 text-11 text-ink-3">
                {tags.length === 0
                  ? 'No tags resolved for this scope.'
                  : `No tags match "${query}".`}
              </div>
            ) : (
              <>
                {filtered.map((tag) => {
                  const value = valueForTag(tag)
                  const property = propertiesByTagId.get(tag.id)
                  const isSelected = selected?.id === tag.id
                  const isUdt = tag.label === 'UDTInstance'
                  return (
                    <button
                      key={tag.id}
                      type="button"
                      onClick={() => setSelectedId(tag.id)}
                      className={cn(
                        'flex flex-col gap-1 rounded-md border bg-card px-2.5 py-2 text-left transition-colors hover:bg-accent',
                        isSelected
                          ? 'border-brand bg-brand-soft/30 ring-2 ring-brand-soft'
                          : 'border-border'
                      )}
                    >
                      <div className="flex min-w-0 items-start gap-1.5">
                        <span className="min-w-0 flex-1 font-mono text-12 font-semibold leading-tight text-ink [overflow-wrap:anywhere]">
                          {pathWithSlashBreaks(fullTagPath(tag))}
                        </span>
                        {property?.kind === 'setpoint' && (
                          <Badge variant="outline" className="shrink-0 text-10">
                            SP
                          </Badge>
                        )}
                        {isUdt && (
                          <Badge variant="secondary" className="shrink-0 text-10">
                            UDT
                          </Badge>
                        )}
                        <SessionEntityMark entityId={tag.id} />
                      </div>
                      <div className="flex items-baseline gap-1">
                        <span
                          className={cn(
                            'font-mono text-sm font-semibold',
                            !isUdt && value?.error ? 'text-st-warn' : 'text-brand'
                          )}
                          title={!isUdt ? value?.error : undefined}
                        >
                          {isUdt ? '—' : formatTagValue(value)}
                        </span>
                        {!isUdt && hasDisplayableValue(value) && (tag.unit || property?.unit) && (
                          <span className="text-11 text-ink-3">{tag.unit ?? property?.unit}</span>
                        )}
                        {isUdt && (
                          <span className="text-11 text-ink-3">
                            whole-template signal · see UDT Instances
                          </span>
                        )}
                      </div>
                      {!isUdt && (
                        <RangeBar
                          value={numericValue(value)}
                          low={property?.range_low}
                          high={property?.range_high}
                        />
                      )}
                    </button>
                  )
                })}
                {tagGhosts.map((ghost) => (
                  <SessionGhostRow
                    key={ghost.id}
                    name={ghost.name ?? ghost.id}
                    view={ghost.view}
                    className="font-mono text-12"
                  />
                ))}
              </>
            )}
          </div>
        </div>
      </aside>
    </>
  )
}

function SelectedTagDetail({
  tag,
  value,
  property,
  unregistered
}: {
  tag: PlantTag
  value: PlantTagValue | undefined
  property: PlantEquipmentProperty | undefined
  unregistered: boolean
}): React.ReactElement {
  const isUdt = tag.label === 'UDTInstance'
  return (
    <div className="shrink-0 border-b px-3.5 py-3">
      <div className="flex min-w-0 items-start gap-1.5">
        <span className="min-w-0 flex-1 font-mono text-sm font-semibold leading-tight text-ink [overflow-wrap:anywhere]">
          {pathWithSlashBreaks(fullTagPath(tag))}
        </span>
        {isUdt && (
          <Badge variant="secondary" className="shrink-0 text-10">
            UDT
          </Badge>
        )}
        <SessionEntityMark entityId={tag.id} />
      </div>
      {isUdt ? (
        <div className="mt-1.5 text-12 text-ink-3">
          This is an Ignition UDT instance — a whole-template signal, not a single readable value.
          Open the <span className="font-semibold text-ink-2">UDT Instances</span> section on this
          equipment to inspect its leaf members.
        </div>
      ) : (
        <>
          <div className="mt-2 flex items-baseline gap-1">
            <span
              className={cn(
                'font-mono text-2xl font-semibold tabular-nums',
                value?.error ? 'text-st-warn' : 'text-brand'
              )}
              title={value?.error}
            >
              {formatTagValue(value)}
            </span>
            {/* The unit only means something next to an actual reading —
                never dangle "s" / "min" / "h" beside a missing value. */}
            {hasDisplayableValue(value) && (
              <span className="text-xs text-ink-3">{tag.unit ?? property?.unit ?? ''}</span>
            )}
          </div>
          <div className="mt-1 flex items-center gap-1.5">
            <Badge variant="outline" className="text-10" title={value?.error}>
              {value?.error ? 'Read failed' : (value?.quality ?? 'No data')}
            </Badge>
            {unregistered && (
              <Badge
                variant="secondary"
                className="text-10"
                title="No registry row is linked to this graph tag ID."
              >
                unregistered
              </Badge>
            )}
            {value?.timestamp && (
              <span className="font-mono text-11 text-ink-3">
                {formatRelativeTimestamp(value.timestamp)}
              </span>
            )}
          </div>
          <div className="mt-2.5">
            <RangeBar
              value={numericValue(value)}
              low={property?.range_low}
              high={property?.range_high}
              showBounds
            />
          </div>
          {/* Health / classification / series enrichment off the registry +
              statistics backend, joined from the graph identity. */}
          <div className="mt-3">
            <PlantTagHealth nodeId={tag.id} tagPath={tagPathFor(tag)} />
          </div>
        </>
      )}
      <div className="mt-3 grid grid-cols-2 gap-1.5 text-11">
        <DetailKV label="Type" value={tag.ignition_tag_type} />
        <DetailKV label="Data" value={tag.data_type} />
        <DetailKV label="Provider" value={tag.provider_name} />
        <DetailKV label="Label" value={tag.label} />
      </div>
    </div>
  )
}

function DetailKV({
  label,
  value
}: {
  label: string
  value: string | undefined
}): React.ReactElement {
  return (
    <div>
      <div className="text-10 font-semibold uppercase tracking-[0.04em] text-ink-3">{label}</div>
      <div className="mt-0.5 truncate font-mono text-12 text-ink-2">{value ?? '—'}</div>
    </div>
  )
}

function RangeBar({
  value,
  low,
  high,
  showBounds
}: {
  value: number | undefined
  low: number | undefined
  high: number | undefined
  showBounds?: boolean
}): React.ReactElement | null {
  const hasRange = typeof low === 'number' && typeof high === 'number' && high > low
  const pct =
    hasRange && typeof value === 'number'
      ? Math.max(0, Math.min(100, ((value - low) / (high - low)) * 100))
      : null
  return (
    <div className="mt-1">
      <div className="relative h-1 overflow-hidden rounded-full bg-bg-sunk">
        {pct !== null && (
          <span
            className="absolute top-1/2 size-2 -translate-y-1/2 rounded-full bg-brand"
            style={{ left: `calc(${pct}% - 0.25rem)` }}
          />
        )}
      </div>
      {showBounds && hasRange && (
        <div className="mt-1 flex justify-between font-mono text-10 text-ink-3">
          <span>{low}</span>
          <span>{high}</span>
        </div>
      )}
    </div>
  )
}

/**
 * Resolve the path used by the proxy's `/live-data/tags` handler (which
 * POSTs `{tagPaths: [...]}` to Ignition WebDev's `getTags`). Ignition
 * expects native tag paths in the form `[provider]folder/tag`.
 * Runtime `:Tag` nodes must carry that canonical value in `tag_path`;
 * display fields and physical OPC addresses never substitute for it.
 *
 * Returns `undefined` for `UDTInstance` entries: those represent a
 * whole-template signal (Ignition UDT instance) and not a single
 * readable leaf value — calling `read_tags` against the instance path
 * would either return the entire member dataset or fail with `Bad_*`.
 * The live panel still renders these in the list (with a "UDT" badge)
 * but skips them from the poll set.
 */
function tagPathFor(tag: PlantTag): string | undefined {
  if (tag.label === 'UDTInstance') return undefined
  // Runtime :Tag nodes, including UDT-instance member tags, carry their
  // canonical live path directly.
  const stored = tag.tag_path
  if (typeof stored === 'string' && stored.length > 0) return stored
  if (tag.label === 'Tag') return undefined
  const candidates = [tag.name, tag.tag_name]
  let path: string | undefined
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.length > 0) {
      path = candidate
      break
    }
  }
  if (!path) return undefined
  if (path.startsWith('[')) return path
  const provider = tag.provider_name
  if (provider) return `[${provider}]${path}`
  return path
}

/**
 * The full, human-readable tag path shown in the browser list:
 * `[Provider]Folder/Tag`. Mirrors {@link tagPathFor} but never returns
 * `undefined` — UDT instances still get a path to display (they're just
 * excluded from the live poll, not from the list).
 */
function fullTagPath(tag: PlantTag): string {
  const stored = tag.tag_path
  if (typeof stored === 'string' && stored.length > 0) return stored
  if (tag.label === 'Tag') return tag.name
  const candidates = [tag.name, tag.tag_name]
  let path: string | undefined
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.length > 0) {
      path = candidate
      break
    }
  }
  if (!path) return tag.name
  if (path.startsWith('[')) return path
  const provider = tag.provider_name
  return provider ? `[${provider}]${path}` : path
}

/**
 * Render `path` so the browser may wrap it onto multiple lines, but only
 * after `/` separators. Each segment keeps its trailing slash and is
 * followed by a `<wbr>` break opportunity; with the default
 * `overflow-wrap: normal`, the otherwise space-less path stays unbroken
 * except at these points.
 */
function pathWithSlashBreaks(path: string): React.ReactNode {
  const segments = path.split('/')
  return segments.map((segment, index) => {
    const isLast = index === segments.length - 1
    return (
      <React.Fragment key={index}>
        {isLast ? segment : `${segment}/`}
        {!isLast && <wbr />}
      </React.Fragment>
    )
  })
}

function numericValue(value: PlantTagValue | undefined): number | undefined {
  if (!value) return undefined
  const raw = value.value
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw
  if (typeof raw === 'string') {
    const parsed = Number.parseFloat(raw)
    return Number.isFinite(parsed) ? parsed : undefined
  }
  return undefined
}

/** True when the reading carries a real value worth pairing a unit with. */
function hasDisplayableValue(value: PlantTagValue | undefined): boolean {
  if (!value || value.error) return false
  const raw = value.value
  if (raw === null || raw === undefined || raw === '') return false
  if (typeof raw === 'number' && !Number.isFinite(raw)) return false
  return true
}

function formatTagValue(value: PlantTagValue | undefined): string {
  if (!value) return '—'
  if (value.error) return 'err'
  const raw = value.value
  if (raw === null || raw === undefined || raw === '') return '—'
  if (typeof raw === 'number') {
    if (!Number.isFinite(raw)) return '—'
    if (Math.abs(raw) >= 1000) return raw.toFixed(0)
    if (Math.abs(raw) >= 10) return raw.toFixed(1)
    return raw.toFixed(2)
  }
  if (typeof raw === 'boolean') return raw ? 'true' : 'false'
  return stringifyTagValue(raw)
}

function formatRelativeTimestamp(timestamp: string): string {
  const ts = Date.parse(timestamp)
  if (Number.isNaN(ts)) return timestamp
  const deltaSec = Math.max(0, Math.round((Date.now() - ts) / 1000))
  if (deltaSec < 5) return 'just now'
  if (deltaSec < 60) return `${deltaSec}s ago`
  const deltaMin = Math.round(deltaSec / 60)
  if (deltaMin < 60) return `${deltaMin}m ago`
  const deltaHr = Math.round(deltaMin / 60)
  return `${deltaHr}h ago`
}
